import { createSelector } from 'reselect';
import { isAfter } from 'date-fns';
import _mapValues from 'lodash/mapValues';
import _cloneDeep from 'lodash/cloneDeep';

// const getSelectedPlan = state => state.selectedPlan;
const getStudentsPlansList = state => state.studentsPlansList;
const getSessions = state => state.sessions;
const getContacts = state => state.contacts;
const getProgress = state => state.progress;

const isOrgProvider = window.cccisd.fortress.user.acting.role.handle === 'orgInstructor';
const learnerHandle = isOrgProvider ? 'orgLearner' : 'learner';

export const getReporterSessions = createSelector(
    [getStudentsPlansList, getSessions, getContacts, (state, plan) => plan],
    (studentsPlansList, sessions, contacts, selectedPlan) => {
        if (!sessions) {
            return null;
        }

        let result = [];
        const sessionsPerStudent = {};

        const planStudents = studentsPlansList.filter(student =>
            student.selectedAssignmentPlanList.some(plan => plan.assignmentPlanId === selectedPlan.assignmentPlanId)
        );

        // Copy all sessions for each student
        // Sessions dont yet have contact info associated with them, aside from the student sessions
        planStudents.forEach(student => {
            sessionsPerStudent[student.pawn.pawnId] = sessions.map(session => {
                const res = {
                    ...session,
                    id: student.pawn.pawnId + session.settings.sessionId,
                    planLabel: selectedPlan.label,
                    pawn: { pawnId: null, pawnHash: null, respondentHash: null },
                    reporter: session.settings.reporter,
                    studentId: student.fields.studentId,
                    timepoint: session.label,
                    isOpen:
                        isAfter(new Date(), new Date(session.opensAt)) &&
                        !isAfter(new Date(), new Date(session.closesAt)),
                    studentContactInfo: {
                        /* eslint-disable no-useless-computed-key */
                        ['pawn.pawnId']: student.pawn.pawnId,
                        ['pawn.pawnHash']: student.pawn.pawnHash,
                        ['childRoles.contactList']: student.childRoles.contactList,
                    },
                };

                if (session.settings.reporter === 'Student') {
                    res.fields = student.fields;
                    res.pawn.pawnId = student.pawn.pawnId;
                    res.pawn.pawnHash = student.pawn.pawnHash;
                    res.pawn.respondentHash = student.pawn.respondentHash;
                    res.name = student.fullName;
                    res.invited = student.messageList.filter(message => message.action === 'assessment_plan_invite');
                    res.reminded = student.messageList.filter(message => message.action === 'assessment_plan_remind');
                    res.email = student.fields.email;
                }

                result.push(res);
                return res;
            });
        });

        // Add contact info to sessions
        // The student may not have any contacts, or there may be multiple contacts for each session
        // If a contact exists for that student/session, hydrate the session with the contact's info
        // Remove the original session from the result and add the new one
        contacts.forEach(contact => {
            if (!sessionsPerStudent[contact.parentRoles[learnerHandle].pawn.pawnId]) {
                return;
            }

            const contactSessions = sessionsPerStudent[contact.parentRoles[learnerHandle].pawn.pawnId].filter(
                session => session.settings.reporter === contact.fields.contactType
            );

            contactSessions.forEach(session => {
                session = sessionsPerStudent[contact.parentRoles[learnerHandle].pawn.pawnId].find(
                    s => s.id === session.id
                );
                const newSession = _cloneDeep(session);
                newSession.id += contact.pawn.pawnId;
                newSession.name = contact.fields.contactName;
                newSession.email = contact.fields.contactEmail;
                newSession.pawn.pawnId = contact.pawn.pawnId;
                newSession.pawn.pawnHash = contact.pawn.pawnHash;
                newSession.pawn.respondentHash = contact.pawn.respondentHash;
                newSession.reporter = contact.fields.contactType;
                newSession.invited = contact.messageList.filter(message => message.action === 'assessment_plan_invite');
                newSession.reminded = contact.messageList.filter(
                    message => message.action === 'assessment_plan_remind'
                );
                result = result.filter(r => r.id !== session.id);
                result.push(newSession);
            });
        });
        return result;
    }
);

export const getReporterProgress = createSelector(
    [(state, plan) => getReporterSessions(state, plan), getProgress],
    (reporterSessions, progress) => {
        if (!progress) {
            return null;
        }

        const result = [];

        const getCompletedSurveys = (session, sessionProgress) => {
            const completedSurveys = [];
            if (sessionProgress) {
                session.settings.options.forEach(surveyHandle => {
                    if (sessionProgress.variables[surveyHandle + '_end']) {
                        completedSurveys.push(surveyHandle);
                    }
                    if (sessionProgress.devTags && sessionProgress.devTags[surveyHandle + '_end']) {
                        completedSurveys.push(surveyHandle);
                    }
                });
            }
            return completedSurveys;
        };

        reporterSessions.forEach(session => {
            // Replace this with assignmentProgress.sessionId ?
            let pawnSessionInfo = session.sessionDeploymentInfo
                ? Object.values(session.sessionDeploymentInfo).find(info => Number(info.pawnId) === session.pawn.pawnId)
                : { deploymentId: null, pawnId: null };

            if (!pawnSessionInfo) {
                pawnSessionInfo = { deploymentId: null, pawnId: null };
            }

            let sessionProgress;
            sessionProgress = progress.find(p => {
                return (
                    p.deployment.deploymentId === pawnSessionInfo.deploymentId &&
                    p.pawn.pawn.pawnId === Number(pawnSessionInfo.pawnId)
                );
            });

            result.push({
                ...session,
                opensAt: session.opensAt,
                closesAt: session.closesAt,
                completed: sessionProgress ? sessionProgress.completedAt : null,
                progress: {
                    total: session.settings.options.length,
                    completed: getCompletedSurveys(session, sessionProgress).length,
                },
            });
        });

        return result;
    }
);

export const getSummaryInfo = createSelector(getReporterProgress, reporterProgress => {
    if (!reporterProgress) {
        return null;
    }

    let summary = {
        students: {
            reporter: 'Students',
            missingContacts: [], // This will always be empty, but is here to make mapping over this summary object easier
        },
        caregivers: {
            reporter: 'Caregivers',
            missingContacts: [],
        },
        educators: {
            reporter: 'Educators',
            missingContacts: [],
        },
        mentalHealthProviders: {
            reporter: 'Mental Health Providers',
            missingContacts: [],
        },
    };

    const getOpenedSessions = sessions => {
        return sessions.filter(session => {
            return session.opensAt && isAfter(new Date(), new Date(session.opensAt));
        });
    };

    const openedSessions = getOpenedSessions(reporterProgress);

    summary = _mapValues(summary, value => {
        value.availableSessions = 0;
        value.completedSessions = 0;
        value.percentCompleted = null;
        return value;
    });

    openedSessions.forEach(session => {
        if (session.reporter === 'Student') {
            summary.students.availableSessions++;
            session.completed && summary.students.completedSessions++;
        }
        if (session.reporter === 'Caregiver') {
            summary.caregivers.availableSessions++;
            session.completed && summary.caregivers.completedSessions++;
            !session.pawn.pawnId && summary.caregivers.missingContacts.push(session.studentId);
        }
        if (session.reporter === 'Educator') {
            summary.educators.availableSessions++;
            session.completed && summary.educators.completedSessions++;
            !session.pawn.pawnId && summary.educators.missingContacts.push(session.studentId);
        }
        if (session.reporter === 'Mental Health Provider') {
            summary.mentalHealthProviders.availableSessions++;
            session.completed && summary.mentalHealthProviders.completedSessions++;
            !session.pawn.pawnId && summary.mentalHealthProviders.missingContacts.push(session.studentId);
        }
    });

    summary = _mapValues(summary, value => {
        if (value.availableSessions > 0) {
            value.percentCompleted = Math.round(
                Number(value.completedSessions / value.availableSessions).toFixed(2) * 100
            );
        }
        return value;
    });

    return summary;
});
